import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './Login.css';
import CityAutocomplete from '../common/CityAutocomplete';
import { auth } from '../../firebase';
import { reload } from 'firebase/auth';
import backgroundImage from '../../assets/common/2nd_background.png';

// Determine API base URL (keep logic consistent with AuthContext and other pages)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  // If no env URL, use localhost for development
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If it's a placeholder URL, use localhost for development
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If env URL contains 'run.app', it's a Cloud Run URL - always use it
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  // In production builds, always use the configured URL
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }

  // In development, use localhost
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function Login() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [activeLoginTab, setActiveLoginTab] = useState('google'); // 'google', 'email', 'phone'
  const [isSignupMode] = useState(false); // Always in login mode
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [needsVerificationCode, setNeedsVerificationCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [businessType, setBusinessType] = useState('');
  const [businessDisplayName, setBusinessDisplayName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [freelancerDisplayName, setFreelancerDisplayName] = useState('');
  const [freelancerPhone, setFreelancerPhone] = useState('');
  const [freelancerLocation, setFreelancerLocation] = useState('');
  const [freelancerLocationValid, setFreelancerLocationValid] = useState(false);
  const [freelancerCategory, setFreelancerCategory] = useState('');
  const [jobCategories, setJobCategories] = useState([]);
  const [businessLocation, setBusinessLocation] = useState('');
  const [businessLocationValid, setBusinessLocationValid] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [checkingDisplayName, setCheckingDisplayName] = useState(false);
  const { login, signup, loginWithGoogle, currentUser, userRoles, addUserRole, logout, loading: authLoading, switchingRole, resendVerificationCode, isApproved, addPassword } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [showAddPasswordForm, setShowAddPasswordForm] = useState(false);
  const [addingPassword, setAddingPassword] = useState(false);
  const [needsGoogleSignIn, setNeedsGoogleSignIn] = useState(false);
  const [pendingEmailForPassword, setPendingEmailForPassword] = useState('');

  // Check if business user is missing business type or display name
  const [needsBusinessType, setNeedsBusinessType] = useState(false);
  const [needsBusinessDisplayName, setNeedsBusinessDisplayName] = useState(false);
  const [checkingBusinessRequirements, setCheckingBusinessRequirements] = useState(false);
  const [businessRequirementsChecked, setBusinessRequirementsChecked] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Sync activeLoginTab with showEmailLogin
  useEffect(() => {
    if (showEmailLogin && activeLoginTab !== 'email') {
      setActiveLoginTab('email');
    } else if (!showEmailLogin && activeLoginTab === 'email') {
      setActiveLoginTab('google');
    }
  }, [showEmailLogin]);

  // Clear verification state when switching away from email login
  useEffect(() => {
    if (activeLoginTab !== 'email') {
      setPassword('');
      setConfirmPassword('');
      setVerificationCode('');
      setNeedsVerificationCode(false);
      setNeedsGoogleSignIn(false);
      setPendingEmailForPassword('');
    }
  }, [activeLoginTab]);

  // Check if user signed in with Google after being prompted to add password
  useEffect(() => {
    if (needsGoogleSignIn && currentUser && currentUser.email) {
      // Check if the email matches the one we were trying to sign in with
      if (pendingEmailForPassword && currentUser.email.toLowerCase() === pendingEmailForPassword.toLowerCase()) {
        // Check if user signed in with Google
        const providerIds = (currentUser.providerData || []).map((p) => p.providerId);
        if (providerIds.includes('google.com')) {
          // User signed in with Google, now show add password form
          setNeedsGoogleSignIn(false);
          setShowAddPasswordForm(true);
          setShowEmailLogin(true); // Ensure email login form is visible
          setLoading(false);
          setIsAuthenticating(false);
          setError(t('login.nowAddPassword') || 'Great! Now you can add a password to your account.');
        }
      }
    }
  }, [needsGoogleSignIn, currentUser, pendingEmailForPassword, t]);

  // Check if business user needs to provide business type or display name
  useEffect(() => {
    const checkBusinessRequirements = async () => {
      // Only check if we have a current user and auth is not loading
      if (!currentUser || authLoading) {
        // If no user or still loading, clear requirements and mark as not checking
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setCheckingBusinessRequirements(false);
        return;
      }

      // Check if user is a business user
      if (userRoles && userRoles.length > 0 && userRoles.includes('business')) {
        setCheckingBusinessRequirements(true);
        setBusinessRequirementsChecked(false);
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../../firebase');
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const missingBusinessType = !userData.businessType || !userData.businessType.trim();
            const missingDisplayName = !userData.displayName || !userData.displayName.trim();

            if (missingBusinessType || missingDisplayName) {
              setNeedsBusinessType(missingBusinessType);
              setNeedsBusinessDisplayName(missingDisplayName);
              setSelectedRole('business');
              if (userData.businessType) setBusinessType(userData.businessType);
              if (userData.displayName) setBusinessDisplayName(userData.displayName);
            } else {
              setNeedsBusinessType(false);
              setNeedsBusinessDisplayName(false);
            }
          } else {
            // User document doesn't exist, assume both are needed
            setNeedsBusinessType(true);
            setNeedsBusinessDisplayName(true);
            setSelectedRole('business');
          }
        } catch (error) {
          console.error('Error checking business requirements:', error);
          // Error checking business requirements
          // On error, assume both are needed to be safe
          setNeedsBusinessType(true);
          setNeedsBusinessDisplayName(true);
          setSelectedRole('business');
        } finally {
          setCheckingBusinessRequirements(false);
          setBusinessRequirementsChecked(true);
        }
      } else if (userRoles && userRoles.length > 0) {
        // User has roles but not business, clear business requirements
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setCheckingBusinessRequirements(false);
        setBusinessRequirementsChecked(true);
      } else {
        // No roles yet or userRoles not loaded, clear business requirements
        // But don't set checkingBusinessRequirements to false if we're still waiting for userRoles
        if (userRoles !== undefined) {
          // userRoles has been loaded (even if empty), so we're done checking
          setNeedsBusinessType(false);
          setNeedsBusinessDisplayName(false);
          setCheckingBusinessRequirements(false);
          setBusinessRequirementsChecked(true);
        }
        // If userRoles is undefined, we're still loading, so leave checkingBusinessRequirements as is
      }
    };
    checkBusinessRequirements();
  }, [currentUser, userRoles, authLoading]);

  // Handle redirect after login and roles/approval have been checked
  useEffect(() => {
    // Wait for auth and business requirements check to finish loading
    if (authLoading || checkingBusinessRequirements) {
      return;
    }

    // Don't redirect if we're showing the add-password form - user needs to add password first
    if (showAddPasswordForm || needsGoogleSignIn) {
      return;
    }

      // Only redirect if user is logged in, approved, and all checks are complete
      const isAdmin = userRoles && userRoles.includes('admin');
      const isApprovedUser = isAdmin || !!isApproved;

      if (currentUser && isApprovedUser && !authLoading && !checkingBusinessRequirements) {
        // userRoles is always an array (initialized as [] in AuthContext)
        // If it has roles, check business requirements and redirect
        if (userRoles && userRoles.length > 0) {
          // Check if business user is missing business type or display name
          // IMPORTANT: For business users, we MUST wait for requirements check to complete
          if (userRoles.includes('business')) {
            // For business users, we must explicitly wait for the requirements check to complete
            if (!businessRequirementsChecked) {
              // Requirements check hasn't completed yet, wait
              return;
            }
            // If requirements are missing, redirect to role selection page
            if (needsBusinessType || needsBusinessDisplayName) {
              setLoading(false);
              setIsAuthenticating(false);
              navigate('/role-selection', { replace: true });
              return;
            }
          }
          // User has at least one role and business type/display name are set (if business), go to appropriate landing page
          setLoading(false);
          setIsAuthenticating(false);
          const defaultPath = userRoles.includes('freelancer')
            ? '/freelancer/dashboard'
            : userRoles.includes('business')
              ? '/business/dashboard'
              : '/profile';
          navigate(defaultPath, { replace: true });
          return;
        }
        // If no roles (empty array) and we're not loading, redirect to role selection
        if (!isAuthenticating) {
          setLoading(false);
          setIsAuthenticating(false);
          navigate('/role-selection', { replace: true });
        }
      } else if (currentUser && !isApprovedUser && !authLoading) {
        // User is logged in but not approved - stay on login page, don't redirect
        // This prevents redirect loops when user comes from unauthorized page
        setLoading(false);
        setIsAuthenticating(false);
      } else if (!currentUser && !authLoading && !isAuthenticating) {
        // User is not logged in and auth is done loading
        // Only reset loading if we're not actively authenticating
        setLoading(false);
      }
    }, [currentUser, userRoles, authLoading, checkingBusinessRequirements, loading, navigate, needsBusinessType, needsBusinessDisplayName, isApproved, businessRequirementsChecked, isAuthenticating]);

  // Clear form fields when switching roles
  useEffect(() => {
    if (switchingRole || (userRoles && userRoles.length === 0)) {
      setBusinessType('');
      setBusinessDisplayName('');
      setBusinessPhone('');
      setFreelancerDisplayName('');
      setFreelancerCategory('');
      setBusinessLocation('');
      setBusinessLocationValid(false);
      setSelectedRole('');
    }
  }, [switchingRole, userRoles]);

  // Load freelancer job categories when freelancer role is selected
  useEffect(() => {
    const fetchJobCategories = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/job-categories`);
        if (!res.ok) return;
        const data = await res.json();
        const categories = Array.isArray(data.categories) ? data.categories : [];
        setJobCategories(categories);
      } catch (e) {
        console.warn('Failed to load job categories', e);
      }
    };

    if (selectedRole === 'freelancer' && jobCategories.length === 0) {
      fetchJobCategories();
    }
  }, [selectedRole, jobCategories.length]);

  // Israeli phone validation
  const isValidIsraeliPhone = (input) => {
    if (!input) return false;
    const normalized = input.replace(/[\s\-]/g, '');
    let local = normalized;
    if (local.startsWith('+972')) {
      local = '0' + local.slice(4);
    }
    const landline = /^0[23489]\d{7}$/;     // e.g., 03-XXXXXXX
    const mobileOrVoip = /^0[57]\d{8}$/;    // e.g., 05X-XXXXXXX, 07X-XXXXXXX
    return landline.test(local) || mobileOrVoip.test(local);
  };
  const handleRoleConfirm = async () => {
    // If needs business type or display name (existing business user), just update them
    if ((needsBusinessType || needsBusinessDisplayName) && currentUser) {
      if (!businessType.trim()) {
        alert(t('login.businessTypeRequired'));
        return;
      }
      if (!businessDisplayName.trim()) {
        alert(t('login.businessDisplayNameRequired'));
        return;
      }
      if (!businessPhone.trim()) {
        alert(t('login.phoneRequired'));
        return;
      }
      if (!isValidIsraeliPhone(businessPhone.trim())) {
        alert(t('login.invalidPhone'));
        return;
      }
      if (!businessLocation.trim() || !businessLocationValid) {
        alert('Please select a city from the list');
        return;
      }

      setSavingRole(true);
      setCheckingDisplayName(true);
      try {
        // Check for duplicate display name via API
        const token = await currentUser.getIdToken();

        // Check if display name is already taken
        const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(businessDisplayName.trim())}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCheckingDisplayName(false);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (!checkData.available) {
            alert(t('login.displayNameTaken'));
            setSavingRole(false);
            return;
          }
        }

        // Use backend API to update profile (ensures validation)
        // Always write businessType and displayName for business users,
        // so that the Firestore "displayName" field matches the business display name.
        const updateData = {
          businessType: businessType.trim(),
          displayName: businessDisplayName.trim(),
          phone: businessPhone.trim(),
          location: businessLocation.trim()
        };

        let updateResponse;
        try {
          const apiUrl = `${API_BASE_URL}/api/user/profile`;
          console.log('Attempting to update profile at:', apiUrl);
          updateResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          });
        } catch (networkError) {
          // Handle network errors (API not reachable, CORS, etc.)
          //console.error.'Network error saving business requirements:', networkError);
          //console.error.'API Base URL being used:', API_BASE_URL);
          //console.error.'Full error:', networkError);
          throw new Error(`Network error: Unable to connect to server at ${API_BASE_URL}. Please check your connection and ensure the backend is running.`);
        }

        if (!updateResponse.ok) {
          let errorMessage = 'Failed to update profile';
          try {
            const errorData = await updateResponse.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
            //console.error.'Profile update error:', errorData);
          } catch (parseError) {
            // If response is not JSON, use status text
            errorMessage = updateResponse.statusText || errorMessage;
            //console.error.'Profile update failed with status:', updateResponse.status, updateResponse.statusText);
          }
          throw new Error(errorMessage);
        }
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setSavingRole(false);
        // After updating profile, wait a moment for Firestore to propagate the update
        // then navigate to business dashboard
        // Using a small delay to allow Firestore write to complete and propagate
        setTimeout(() => {
          navigate('/business/dashboard', { replace: true });
        }, 1000);
      } catch (error) {
        // Error saving business requirements
        // Show specific error messages
        if (error.message && error.message.includes('Display name already taken')) {
          alert(t('login.displayNameTaken'));
        } else if (error.message && error.message.includes('Display name is required')) {
          alert(t('login.businessDisplayNameRequired'));
        } else if (error.message && error.message.includes('Unauthorized')) {
          alert(t('login.failedToSaveRole') + ' (Unauthorized)');
        } else if (error.message && error.message.includes('Network error')) {
          alert(error.message);
        } else if (error.message && error.message.includes('Failed to update profile')) {
          alert(error.message);
        } else {
          alert(t('login.failedToSaveRole') + (error.message ? ': ' + error.message : ''));
        }
        setSavingRole(false);
        setCheckingDisplayName(false);
      }
      return;
    }

    if (selectedRole && currentUser) {
      // Get display name based on role
      const displayName = selectedRole === 'business' ? businessDisplayName.trim() : freelancerDisplayName.trim();

      // Validate based on role
      if (selectedRole === 'business') {
        if (!businessType.trim()) {
          alert(t('login.businessTypeRequired'));
          return;
        }
        if (!displayName) {
          alert(t('login.businessDisplayNameRequired'));
          return;
        }
        if (!businessPhone.trim()) {
          alert(t('login.phoneRequired'));
          return;
        }
        if (!isValidIsraeliPhone(businessPhone.trim())) {
          alert(t('login.invalidPhone'));
          return;
        }
        if (!businessLocation.trim() || !businessLocationValid) {
          alert('Please select a city from the list');
          return;
        }
      } else if (selectedRole === 'freelancer') {
        if (!displayName) {
          alert(t('login.displayNameRequired'));
          return;
        }
        if (!freelancerPhone.trim()) {
          alert(t('login.phoneRequired'));
          return;
        }
        if (!isValidIsraeliPhone(freelancerPhone.trim())) {
          alert(t('login.invalidPhone'));
          return;
        }
        if (!freelancerLocation.trim() || !freelancerLocationValid) {
          alert('Please select a city from the list');
          return;
        }
        if (jobCategories.length > 0 && !freelancerCategory) {
          alert(t('login.freelancerCategoryRequired') || 'Please select a category');
          return;
        }
      }

      setSavingRole(true);
      setCheckingDisplayName(true);
      try {
        // Check for duplicate display name before adding role (for both business and freelancer)
        const token = await currentUser.getIdToken();

        const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(displayName)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCheckingDisplayName(false);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (!checkData.available) {
            alert(t('login.displayNameTaken'));
            setSavingRole(false);
            return;
          }
        }

        console.log('Attempting to add role:', selectedRole);
        await addUserRole(
          currentUser.uid,
          selectedRole,
          selectedRole === 'business' ? businessType.trim() : null,
          displayName
        );
        console.log('Role added, navigating to dashboard');
        // Save contact info if applicable
        try {
          if (selectedRole === 'freelancer') {
            const token2 = await currentUser.getIdToken();
            await fetch(`${API_BASE_URL}/api/user/profile`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token2}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                displayName,
                phone: freelancerPhone.trim(),
                location: freelancerLocation.trim(),
                freelancerCategory: freelancerCategory || null
              })
            });
          } else if (selectedRole === 'business') {
            const token2 = await currentUser.getIdToken();
            await fetch(`${API_BASE_URL}/api/user/profile`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token2}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                displayName,
                businessType: businessType.trim(),
                phone: businessPhone.trim(),
                location: businessLocation.trim()
              })
            });
          }
        } catch (err) {
          console.warn('Failed to save freelancer contact info', err);
        }
        const targetPath = selectedRole === 'freelancer' ? '/freelancer/dashboard' : '/business/dashboard';
        navigate(targetPath);
      } catch (error) {
        //console.error.'Error adding role:', error);
        if (error.message && error.message.includes('Display name already taken')) {
          alert(t('login.displayNameTaken'));
        } else {
          alert(t('login.failedToSaveRole'));
        }
        setSavingRole(false);
        setCheckingDisplayName(false);
      }
    }
  };

  async function handleVerifyCode() {
    // Set loading immediately for instant feedback
    setLoading(true);
    setIsAuthenticating(true);
    setError('');

    if (!currentUser) {
      setError(t('login.pleaseSignInToVerify') || 'Please sign in first to verify your email');
      setLoading(false);
      setIsAuthenticating(false);
      return;
    }

    if (!verificationCode.trim() || verificationCode.trim().length !== 6) {
      setError(t('login.invalidVerificationCode') || 'Please enter a valid 6-digit verification code');
      setLoading(false);
      setIsAuthenticating(false);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verificationCode.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify code');
      }

      // Verification successful - reload user to get updated approval status
      await reload(currentUser);
      setNeedsVerificationCode(false);
      setVerificationCode('');
      setError('');
      // Keep loading true - useEffect will handle redirect
      // Keep isAuthenticating true until redirect happens
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || t('login.invalidVerificationCode') || 'Invalid verification code. Please try again.');
      setIsAuthenticating(false);
      setLoading(false);
    }
  }

  async function handleEmailPasswordLogin() {
    // Set loading immediately for instant feedback
    setLoading(true);
    setIsAuthenticating(true);
    setError('');

    try {
      if (!email.trim()) {
        setError(t('login.emailRequired') || 'Email is required');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }
      if (!password.trim()) {
        setError(t('login.passwordRequired') || 'Password is required');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }

      try {
        // First attempt: without code (or with code if we already know it's needed)
        const codeToSend = needsVerificationCode ? verificationCode.trim() || null : null;
        await login(email.trim(), password.trim(), codeToSend);

        // If login succeeds here, either email was already verified, or code was valid
        setNeedsVerificationCode(false);
        setVerificationCode('');
        // Keep loading true - the useEffect will handle redirect and loading screen
        // The loading screen check will show based on authLoading state
        // Don't set loading to false here, let authLoading handle the transition
        // Keep isAuthenticating true until redirect happens
      } catch (err) {
        console.error('Email/password login error:', err);

        if (err.code === 'auth/code-required') {
          // Backend has sent a verification code; prompt user to enter it
          // Keep loading false so user can enter code
          setLoading(false);
          setIsAuthenticating(false);
          setNeedsVerificationCode(true);
          setError(t('login.verifyEmailCodeInstructions') || 'Please check your email and enter the 6-digit verification code.');
        } else if (err.code === 'auth/google-account-no-password') {
          // This email is registered with Google but has no password
          // Prompt user to sign in with Google first
          setLoading(false);
          setIsAuthenticating(false);
          setNeedsGoogleSignIn(true);
          setPendingEmailForPassword(email.trim());
          setError(t('login.googleAccountNoPassword') || 'This email is registered with Google. Please sign in with Google first, then you can add a password.');
        } else {
          // Set loading to false on other errors
          setLoading(false);
          setIsAuthenticating(false);

          if (err.code === 'auth/invalid-verification-code') {
            setError(t('login.invalidVerificationCode') || err.message || 'Invalid verification code. Please try again.');
          } else if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
            setError(t('login.wrongPasswordOrEmail') || 'Incorrect email or password.');
          } else if (err.code === 'auth/too-many-requests') {
            setError(t('login.tooManyRequests') || 'Too many failed login attempts. Please try again later.');
          } else if (err.code === 'auth/operation-not-allowed') {
            // Email/password sign-in not enabled or misconfigured
            setError(t('login.emailPasswordNotEnabled') || 'Email/Password authentication is not enabled. Please contact support.');
          } else if (err.message === 'failed-to-send-code') {
            setError(t('login.failedToSendCode') || 'Failed to send verification code. Please try again later.');
          } else {
            const msg = err.message || err.toString();
            setError((t('login.failedToLogin') || 'Failed to login') + ': ' + msg);
          }
        }
      }
    } catch (outerErr) {
      console.error('Unexpected error in email/password login:', outerErr);
      setError(t('login.failedToLogin') || 'Failed to login');
      setLoading(false);
      setIsAuthenticating(false);
    }
  }

  async function handleAddPassword() {
    setAddingPassword(true);
    setError('');

    try {
      if (!password.trim()) {
        setError(t('login.passwordRequired') || 'Password is required');
        setAddingPassword(false);
        return;
      }

      if (password.length < 6) {
        setError(t('login.passwordTooShort') || 'Password must be at least 6 characters');
        setAddingPassword(false);
        return;
      }

      if (password !== confirmPassword) {
        setError(t('login.passwordsDoNotMatch') || 'Passwords do not match');
        setAddingPassword(false);
        return;
      }

      // Check if user is already authenticated
      if (!currentUser) {
        // User needs to sign in with Google first
        setError(t('login.signInWithGoogleFirst') || 'Please sign in with Google first, then you can add a password. Click "Continue with Google" above.');
        setAddingPassword(false);
        return;
      }

      // Check if user signed in with Google
      const providerIds = (currentUser.providerData || []).map((p) => p.providerId);
      if (!providerIds.includes('google.com')) {
        setError(t('login.addPasswordOnlyGoogle') || 'This feature is only available for Google-authenticated users. Please sign in with Google first.');
        setAddingPassword(false);
        return;
      }

      // Add password
      const result = await addPassword(password.trim());

      setError(t('login.passwordAddedSuccess') || 'Password added successfully! A verification code has been sent to your email. You will need to verify your email when signing in with email and password.');
      setShowAddPasswordForm(false);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error adding password:', err);
      setError(err.message || t('login.failedToAddPassword') || 'Failed to add password');
    } finally {
      setAddingPassword(false);
    }
  }

  async function handleGoogleLogin() {
    try {
      setError('');
      setLoading(true);
      setIsAuthenticating(true);
      // Ensure loading state is set immediately
      await new Promise(resolve => setTimeout(resolve, 0));
      const result = await loginWithGoogle();
      if (result && result.user) {
        // Give a moment for roles to load via AuthContext
        // The useEffect will handle the redirect once roles are loaded
        // Keep isAuthenticating true until redirect happens
      } else {
        setLoading(false);
        setIsAuthenticating(false);
      }
    } catch (error) {
      // Show more detailed error message
      let errorMessage = error.message || error.toString();
      let userFriendlyMessage = errorMessage;
      // Handle specific error codes
      if (error.code === 'auth/invalid-credential') {
        userFriendlyMessage = t('login.invalidCredentialError') + ' ' + t('login.invalidCredentialHelp');
      } else if (error.code === 'auth/popup-blocked') {
        userFriendlyMessage = t('login.popupBlocked');
      } else if (error.code === 'auth/popup-closed-by-user') {
        userFriendlyMessage = t('login.popupClosed');
      } else if (error.code === 'auth/unauthorized-domain') {
        userFriendlyMessage = t('login.unauthorizedDomain');
      } else if (error.code === 'auth/operation-not-allowed') {
        userFriendlyMessage = t('login.googleNotEnabled');
      } else if (error.code === 'auth/internal-error' || errorMessage.includes('internal-error')) {
        userFriendlyMessage = t('login.internalError');
      }

      setError(t('login.failedToLogin') + ': ' + userFriendlyMessage);
      setLoading(false);
      setIsAuthenticating(false);
    }
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      //console.error.'Error logging out:', error);
      setError(t('login.failedToLogout') || 'Failed to logout');
    }
  };

  // Show loading state while checking auth and roles or business requirements
  if (authLoading || loading || checkingBusinessRequirements || switchingRole) {
    return (
      <div className={`loading-screen ${isRTL ? 'rtl' : ''}`}>
        <div className="loading-spinner-container">
          <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', borderColor: '#2563EB', borderTopColor: '#60A5FA' }}></span>
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show login if user is not logged in
  return (
    <div className={`login-container ${isRTL ? 'rtl' : ''}`}>
      {/* Top Section: Logo and Language Switcher */}
      <br/>
      <br/>
      <br/>

      {/* App Name and Slogan */}
      <div className="login-header">
        <h1 className="login-header-app-name">{t('app.name')}</h1>
        <h2 className="login-page-title">{t('login.pageTitle')}</h2>
        <p className="login-header-tagline">{t('app.tagline')}</p>
      </div>

      {/* Authentication Options */}
      <div className="login-auth-options-container">
        <div className="login-tabs-container">
          <div className="login-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeLoginTab === 'google'}
              className={`login-tab ${activeLoginTab === 'google' ? 'active' : ''}`}
              onClick={() => {
                if (!loading) {
                  setActiveLoginTab('google');
                  setShowEmailLogin(false);
                }
              }}
              disabled={loading}
            >
              <div className="login-tab-icon">
                <svg className="google-icon-colored" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span className="login-tab-label">{t('login.google') || 'Google'}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeLoginTab === 'email'}
              className={`login-tab ${activeLoginTab === 'email' ? 'active' : ''}`}
              onClick={() => {
                if (!loading) {
                  setActiveLoginTab('email');
                  setShowEmailLogin(true);
                }
              }}
              disabled={loading}
            >
              <div className="login-tab-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <span className="login-tab-label">{t('login.email') || 'Email'}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeLoginTab === 'phone'}
              className={`login-tab ${activeLoginTab === 'phone' ? 'active' : ''}`}
              onClick={() => {
                if (!loading) {
                  setActiveLoginTab('phone');
                  setShowEmailLogin(false);
                }
              }}
              disabled={loading}
            >
              <div className="login-tab-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <span className="login-tab-label">{t('login.phone') || 'Phone'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Login Form Card */}
      <div className="login-card">
        {error && <div className="error-message">{error}</div>}

        {needsGoogleSignIn && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            borderRadius: '8px',
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            textAlign: 'center'
          }}>
            <p style={{ color: '#111827', marginBottom: '1rem', fontSize: '0.9rem' }}>
              {t('login.googleAccountNoPassword') || 'This email is registered with Google. Please sign in with Google first, then you can add a password.'}
            </p>
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="google-button"
              style={{
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? 'none' : 'auto',
                width: '100%',
                marginTop: '0.5rem'
              }}
            >
              {loading ? (
                <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'rgba(255,255,255,0.9)' }}></span>
              ) : (
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="google-button-text">
                {loading ? t('login.signingIn') : t('login.signInWithGoogle')}
              </span>
            </button>
          </div>
        )}


        {activeLoginTab === 'email' ? (
          <div className="email-login-form" style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                flexDirection: 'column',
                gap: '1.5rem',
                border: '2px solid rgba(37, 99, 235, 0.3)',
                boxShadow: '0 0 20px rgba(37, 99, 235, 0.2)'
              }}>
                <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '4px', borderColor: 'rgba(37, 99, 235, 0.2)', borderTopColor: '#2563EB' }}></span>
                <span style={{ color: '#111827', fontSize: '1rem', fontWeight: '500' }}>
                  {needsVerificationCode && currentUser
                    ? (t('login.verifying') || 'Verifying...')
                    : (t('login.signingIn') || 'Signing in...')
                  }
                </span>
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '12px',
                  border: '1px solid #D1D5DB',
                  background: 'white',
                  color: '#111827',
                  fontSize: '1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                {t('login.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder') || 'Enter your password'}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '12px',
                  border: '1px solid #D1D5DB',
                  background: 'white',
                  color: '#111827',
                  fontSize: '1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEmailPasswordLogin();
                  }
                }}
              />
            </div>


            {needsVerificationCode && (
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="verification-code" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                  {t('login.verificationCode') || 'Verification Code'}
                </label>
                <input
                  id="verification-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  placeholder="123456"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '2px solid #E5E7EB',
                    background: 'white',
                    color: '#111827',
                    fontSize: '1rem',
                    letterSpacing: '0.3em',
                    textAlign: 'center'
                  }}
                  disabled={loading}
                  autoComplete='off'
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (currentUser && needsVerificationCode) {
                        handleVerifyCode();
                      } else {
                        handleEmailPasswordLogin();
                      }
                    }
                  }}
                />
                <p style={{ color: '#6B7280', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  {t('login.verifyEmailCodeInstructions') || 'Please check your email and enter the 6-digit verification code.'}
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    setResendingCode(true);
                    setError('');
                    try {
                      await resendVerificationCode(email.trim());
                      setError(t('login.verificationCodeResent') || 'Verification code has been resent!');
                    } catch (err) {
                      setError(err.message || t('login.failedToResendCode') || 'Failed to resend code');
                    } finally {
                      setResendingCode(false);
                    }
                  }}
                  disabled={resendingCode || !email.trim()}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    marginTop: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    background: 'transparent',
                    color: '#111827',
                    fontSize: '0.9rem',
                    cursor: (resendingCode || !email.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (resendingCode || !email.trim()) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  {resendingCode && (
                    <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'rgba(255,255,255,0.9)' }}></span>
                  )}
                  {resendingCode ? (t('login.resending') || 'Resending...') : (t('login.resendCode') || 'Resend Code')}
                </button>
              </div>
            )}

            {showAddPasswordForm && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '8px',
                background: '#EFF6FF',
                border: '1px solid #BFDBFE'
              }}>
                <p style={{ color: '#111827', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  {t('login.addPasswordToGoogleAccount') || 'Add a password to your Google account so you can sign in with email and password:'}
                </p>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-password" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                    {t('login.password') || 'Password'}
                  </label>
                  <input
                    id="add-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordPlaceholder') || 'Enter password'}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB',
                      background: 'white',
                      color: '#111827',
                      fontSize: '1rem'
                    }}
                    disabled={addingPassword}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-confirm-password" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                    {t('login.confirmPassword') || 'Confirm Password'}
                  </label>
                  <input
                    id="add-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('login.confirmPasswordPlaceholder') || 'Confirm password'}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB',
                      background: 'white',
                      color: '#111827',
                      fontSize: '1rem'
                    }}
                    disabled={addingPassword}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !addingPassword && password.trim() && confirmPassword.trim()) {
                        handleAddPassword();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleAddPassword}
                  disabled={addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: (addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword)
                      ? '#E5E7EB'
                      : '#2563EB',
                    color: (addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword)
                      ? '#9CA3AF'
                      : 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: (addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword)
                      ? 'not-allowed'
                      : 'pointer',
                    marginBottom: '0.5rem'
                  }}
                >
                  {addingPassword ? (t('login.addingPassword') || 'Adding Password...') : (t('login.addPassword') || 'Add Password')}
                </button>
                <button
                  onClick={() => {
                    setShowAddPasswordForm(false);
                    setPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    background: 'transparent',
                    color: '#111827',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            )}

            {(() => {
              // Calculate button state
              const isAddPasswordDisabled = showAddPasswordForm && (
                addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword
              );
              const isLoginDisabled = !showAddPasswordForm && (
                loading ||
                (needsVerificationCode && currentUser
                  ? verificationCode.trim().length !== 6
                  : !email.trim() ||
                    !password.trim() ||
                    (needsVerificationCode && verificationCode.trim().length !== 6))
              );
              const isDisabled = isAddPasswordDisabled || isLoginDisabled;
              const buttonBackground = isDisabled ? '#9CA3AF' : '#374151';
              const buttonCursor = isDisabled ? 'not-allowed' : 'pointer';

              return (
                <button
                  onClick={
                    showAddPasswordForm
                      ? handleAddPassword
                      : needsVerificationCode && currentUser
                        ? handleVerifyCode
                        : handleEmailPasswordLogin
                  }
                  disabled={isDisabled}
                  style={{
                    width: '100%',
                    padding: '1rem 1.5rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: buttonBackground,
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: buttonCursor,
                    marginBottom: '0',
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.5 : 1,
                    pointerEvents: loading ? 'none' : 'auto',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
              {loading && (
                <div
                  className="signin-loading-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '2px solid rgba(37, 99, 235, 0.5)',
                    boxShadow: '0 0 30px rgba(37, 99, 235, 0.4), inset 0 0 20px rgba(37, 99, 235, 0.1)',
                    animation: 'fadeInOverlay 0.3s ease-out'
                  }}
                >
                  <span
                    className="spinner"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderWidth: '4px',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                      borderTopColor: '#2563EB',
                      animation: 'spin 0.8s linear infinite',
                      display: 'block'
                    }}
                  ></span>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{
                      color: '#111827',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      letterSpacing: '0.3px',
                      display: 'block'
                    }}>
                      {needsVerificationCode && currentUser
                        ? (t('login.verifying') || 'Verifying...')
                        : (t('login.signingIn') || 'Signing in...')}
                    </span>
                    <span style={{
                      color: '#6B7280',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      display: 'block'
                    }}>
                      {t('login.pleaseWait') || 'Please wait while we sign you in'}
                    </span>
                  </div>
                </div>
              )}
                  <span style={{ opacity: (loading || addingPassword) ? 0 : 1, position: 'relative', zIndex: 1 }}>
                    {showAddPasswordForm
                      ? (t('login.addPassword') || 'Add Password')
                      : needsVerificationCode && currentUser
                        ? (t('login.verifyEmail') || 'Verify Email')
                        : (t('login.signInButton') || 'Sign in')}
                  </span>
                </button>
              );
            })()}

          </div>
        ) : activeLoginTab === 'google' ? (
          <div style={{ position: 'relative', width: '100%', marginTop: 'var(--spacing-lg)' }}>
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="google-button"
              style={{
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? 'none' : 'auto',
                position: 'relative',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
            >
              {loading && (
                <div
                  className="signin-loading-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '2px solid rgba(37, 99, 235, 0.5)',
                    boxShadow: '0 0 30px rgba(37, 99, 235, 0.4), inset 0 0 20px rgba(37, 99, 235, 0.1)',
                    animation: 'fadeInOverlay 0.3s ease-out'
                  }}
                >
                  <span
                    className="spinner"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderWidth: '4px',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                      borderTopColor: '#2563EB',
                      animation: 'spin 0.8s linear infinite',
                      display: 'block'
                    }}
                  ></span>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{
                      color: '#111827',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      letterSpacing: '0.3px',
                      display: 'block'
                    }}>
                      {t('login.signingIn') || 'Signing in...'}
                    </span>
                    <span style={{
                      color: '#6B7280',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      display: 'block'
                    }}>
                      {t('login.pleaseWait') || 'Please wait while we sign you in'}
                    </span>
                  </div>
                </div>
              )}
              {loading ? (
                <span
                  className="spinner spinner-small"
                  style={{
                    marginRight: 0,
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    width: '20px',
                    height: '20px',
                    borderWidth: '3px'
                  }}
                ></span>
              ) : (
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="google-button-text">
                {loading ? t('login.signingIn') : t('login.signInWithGoogle')}
              </span>
            </button>

            <div className="login-footer">
              <p>{t('login.signInDescription')}</p>
            </div>
          </div>
        ) : (
          <div style={{ position: 'relative', width: '100%', marginTop: 'var(--spacing-lg)', padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
            <p>{t('login.phoneLoginComingSoon') || 'Phone login is coming soon!'}</p>
          </div>
        )}
        <button
          onClick={() => navigate('/auth-choice')}
          className="back-to-homepage-btn"
        >
          {t('login.backToHomepage') || 'Back'}
        </button>

      </div>
    </div>
  );
}
